import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import {
  ButtonActionType,
  ChannelType,
  IPublishedForm,
} from '../../../modules/templates/interfaces/create-template.interface';
import {
  EXTENSION,
  MEDIA_TYPE,
  SHORTEN_URL_CONST,
} from 'src/app/core/constants/common';
import { Icons } from 'src/app/shared/utilities/icons';
import { MaxShowLetterCount } from '../../utilities/constants';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-template-preview',
  templateUrl: './template-preview.component.html',
  styleUrls: ['./template-preview.component.scss'],
})
export class TemplatePreviewComponent implements OnInit, OnChanges {
  @Input() channel: string;
  @Input() mediaURL?: string;
  @Input() content: string;
  @Input() footer: string;
  @Input() ctaButtons?: any[];
  @Input() isCarousel: boolean;
  @Input() carouselCards: any[];
  @Input() isCalledFromCreation = false;
  @Input() activeForm: IPublishedForm | null;

  icons = Icons;
  mediaType = MEDIA_TYPE;
  buttonActionType = ButtonActionType;
  mediaFileType: string;
  isTextExpanded = false;
  channelType = ChannelType;
  maxLetterCount: number;
  carouselUICards: any[];
  pdfFileName: string = '';

  ngOnInit(): void {
    this.maxLetterCount =
      this.channel === ChannelType.WHATSAPP
        ? MaxShowLetterCount.WHATSAPP
        : MaxShowLetterCount.SMS;

    // Handle initial mediaURL if it exists
    if (this.mediaURL && this.mediaURL !== '#mediaUrl#') {
      this.setTypeAndExtension(this.mediaURL);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mediaURL']) {
      this.mediaURL = changes['mediaURL'].currentValue;
      if (this.mediaURL && this.mediaURL !== '#mediaUrl#') {
        this.setTypeAndExtension(this.mediaURL);
      } else {
        // Clear media type if no valid URL
        this.mediaFileType = '';
      }
    }
    if (changes['footer']?.currentValue?.length) {
      this.footer = changes['footer'].currentValue;
    }
    if (changes['ctaButtons']?.currentValue?.length) {
      this.ctaButtons = changes['ctaButtons'].currentValue;
    }
    if (changes['isCarousel']) {
      this.isCarousel = changes['isCarousel'].currentValue;
    }
    if (
      changes['carouselCards']?.currentValue?.length &&
      JSON.stringify(changes['carouselCards'].previousValue) !==
        JSON.stringify(changes['carouselCards']?.currentValue)
    ) {
      this.carouselCards = changes['carouselCards'].currentValue;
      this.carouselUICards = [...this.carouselCards];
      if (this.carouselUICards?.length && this.isCalledFromCreation) {
        this.modifyCarouselCards();
      }
    }

    if (this.content) {
      this.content = this.getUpdatedFormNameContent(this.content);
    }
  }

  setTypeAndExtension(mediaURL: string): void {
    if (!mediaURL || mediaURL.trim() === '') {
      this.mediaFileType = '';
      this.pdfFileName = '';
      return;
    }

    // Don't process blob URLs as they don't have traditional file extensions
    if (mediaURL.startsWith('blob:')) {
      this.mediaFileType = '';
      this.pdfFileName = '';
      return;
    }

    const extension = mediaURL.split('.').pop()?.toUpperCase();

    if (!extension) {
      this.mediaFileType = '';
      this.pdfFileName = '';
      return;
    }

    if (extension === EXTENSION.MP4) {
      this.mediaFileType = MEDIA_TYPE.VIDEO;
      this.pdfFileName = '';
    } else if (extension === EXTENSION.PNG || extension === EXTENSION.JPEG) {
      this.mediaFileType = MEDIA_TYPE.IMAGE;
      this.pdfFileName = '';
    } else {
      this.mediaFileType = MEDIA_TYPE.DOC;
      this.pdfFileName = this.getFileNameFromURL(mediaURL);
    }
  }

  getFileNameFromURL(url: string): string {
    const name = url
      ?.slice(url.lastIndexOf('/') + 1, url.lastIndexOf('_'))
      ?.replaceAll('%20', ' ');
    const arr = url?.split('.');
    const extension = arr[arr.length - 1];
    return `${name}.${extension}`;
  }

  toggleText(): void {
    this.isTextExpanded = !this.isTextExpanded;
  }

  modifyCarouselCards(): void {
    this.carouselUICards = this.carouselCards.map((card) => {
      return {
        ...card,
        mediaURL: card.cardImage || '',
        content: card.cardText,
        mediaType: card.cardImage?.includes('.mp4')
          ? MEDIA_TYPE.VIDEO
          : MEDIA_TYPE.IMAGE,
        buttons: card.button?.map((cta: any) => {
          return {
            ...cta,
            buttonAction: cta?.action?.replace(/_/g, ' '),
          };
        }),
      };
    });
  }

  getUpdatedFormNameContent(content: string | undefined): string {
    if (!content) {
      return '';
    }
    const { formName } = this.activeForm || {};
    const formCode = this.activeForm?.formCode;

    if (formName && formCode) {
      const formUrl = `<a href="${environment.FORM_PREVIEW_URL}/form/${formCode}/PREVIEW" target="_blank">${formName} </a>`;
      return content
        .replace('#form_url#', formUrl)
        .replace('#form_url_tiny#', formUrl);
    } else {
      return content;
    }
  }

  getButtonUrl(button: any, action: string): string | undefined {
    const prefixes = ['http://', 'https://'];
    let url: string | undefined;

    switch (action) {
      case 'STATIC CTA':
      case this.buttonActionType.STATIC_CTA: {
        url = (button?.url || button?.buttonValue)?.trim();
        if (url && !prefixes.some((prefix) => url?.startsWith(prefix))) {
          url = `https://${url}`;
        }
        break;
      }
      case 'DYNAMIC CTA':
      case this.buttonActionType.DYNAMIC_CTA: {
        url = button?.urlShortenWhatsapp
          ? button.buttonValue
          : (button?.url || button?.buttonValue)?.trim();
        if (url && !prefixes.some((prefix) => url?.startsWith(prefix))) {
          url = `https://${url}`;
        }
        break;
      }
      case 'WEB FORM URL':
      case this.buttonActionType.WEB_FORM_URL: {
        const formCode = button?.buttonValue || button?.formCode;
        if (formCode) {
          url = `${environment.FORM_PREVIEW_URL}/form/${formCode}/PREVIEW/`;
        }
        break;
      }
    }
    return url;
  }

  openButtonAction(button: any, isCard?: boolean): void {
    const url = this.getButtonUrl(button, button?.buttonAction.toUpperCase());

    if (url) {
      window.open(url, '_blank');
    }
  }
}
